'use strict';

/**
 * Model for a set item
 *
 * @category   Model
 * @module     collection
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Model = require('base/Colido.Model');

var SetModel = Model.extend({
    api: '/sets',

    urlRoot: false, //needed to get proper api endpoint for models which are part of subcollection (catalog.objects...) with dynamic api urls

    /**
     * Default values
     * @type {Object}
     */
    defaults: function() {
        return {
            _empty: false,
            name: '', //App.t('set:defaultName'), //@todo init this after App.t was added
            count: 0,
            image: null,
            items: [],
            shareToken: null,
        };
    },

    /**
     * Indicates, if the set has at least 1 image
     * @returns {boolean} True, if the set has an image
     */
    hasImage: function() {
        return this.get('image') || (this.get('itemImages') || []).length > 0;
    },

    /**
     * Stub for checking, if the set has subsets
     * @returns {boolean} True, if the set has an subset, otherwise false
     */
    hasSubSet: function() {
        return false;
    },

    isShared: function() {
        return this.get('shareToken') !== null;
    },

    getImage: function() {
        var image = this.get('image');
        var itemImages = this.get('itemImages') || [];

        if (!image && itemImages.length > 0) {
            image = _.first(itemImages);
        }

        if (image) {
            return {
                baseUrl: image.baseUrl,
                fileName: image.fileName,
            };
        }

        return null;
    },

    getImageUrl: function(size) {
        size = size || '640x640';

        var image = this.get('image');
        var itemImages = this.get('itemImages') || [];

        if (itemImages.length > 0) {
            image = _.first(itemImages);
        }

        if (image) {
            return image.baseUrl + size + '/' + image.fileName;
        }

        return null; //@todo return placeholder image
    },

    /**
     * Shorthand method to add items to set
     * @param {Array} objectIds The objects that should be added
     * @param succesCallback Success-Callback for operation
     */
    addItems: function(objectIds, succesCallback, errorCallback) {
        objectIds = _.isArray(objectIds) ? objectIds : [objectIds];

        // We will fetch newest data from server, in case someone changed objects
        this.fetch({
            success: function(model) {
                var currentItems = model.get('items');
                var catalog = this.collection.catalog;

                // Add new items
                _.each(objectIds, function(id) {
                    if (!_.contains(currentItems, id)) {
                        currentItems.push(id);
                        var objectModel = catalog.objects.get(id);
                        if (objectModel) { // Check, because objectModel could be already deleted
                            objectModel.trigger('change:sets'); //@todo Maybe decouple via App.vent trigger. Listen for it in objects collection?
                        }
                    }
                }.bind(this));

                // Save and notify succesCallback
                this.save({items: currentItems, patchType: 'add'}, { // ONLY ADD per PATCH
                    patch: true,
                    success: function(model) {
                        if (typeof succesCallback === 'function') {
                            succesCallback.call(this, model);
                        }

                        this.trigger('change:items', this, this.get('items'));
                    }.bind(this),
                });
            }.bind(this),
            error: function(model) {
                if (_.isFunction(errorCallback)) {
                    errorCallback.call(this, model);
                }
            }.bind(this),
        });
    },

    /**
     * Shorthand method to remove items from set
     * @param {Array} objectIds The objects that should be added
     * @param {Function} succesCallback Callback, when operation was successful
     * @param {Function} errorCallback  Callback, when operation was not successful
     */
    removeItems: function(objectIds, succesCallback, errorCallback) {
        objectIds = _.isArray(objectIds) ? objectIds : [objectIds];

        // We will fetch newest data from server, in case someone changed objects
        this.fetch({
            success: function(model) {
                var currentItems = model.get('items');
                var catalog = this.collection.catalog;

                // Remove Items
                _.each(objectIds, function(id) {
                    if (_.contains(currentItems, id)) {
                        currentItems = _.without(currentItems, id);
                        var objectModel = catalog.objects.get(id);
                        if (objectModel) { // Check, because objectModel could be already deleted
                            objectModel.trigger('change:sets'); //@todo Maybe decouple via App.vent trigger. Listen for it in objects collection?
                        }
                    }
                }.bind(this));

                // Save and notify callback
                this.save({items: currentItems, patchType: 'remove'}, { // ONLY REMOVE per PATCH
                    patch: true,
                    success: function(model) {
                        if (typeof succesCallback === 'function') {
                            succesCallback.call(this, model);
                        }

                        this.trigger('change:items', this, this.get('items'));
                    }.bind(this),
                    error: function(model) {
                        if (_.isFunction(errorCallback)) {
                            errorCallback.call(this, model);
                        }
                    }.bind(this),
                });
            }.bind(this),
        });
    },

    getObjects: function() {
        var setItems = this.get('items');

        return this.collection.catalog.objects.filter(function(object){
            return _.indexOf(setItems, object.id) > -1;
        });
    },

    getSum: function() {
        var sumField = this.get('sumField');
        var setCurrency = this.get('setCurrency');

        return _.reduce(this.getObjects(), function (memo, object) {
            return memo + object.getConvertedMonetaryValue(sumField, setCurrency);
        }, 0);
    },
});

module.exports = SetModel;